/**
 * Plan-selection and plan-comparison logic (platform-agnostic).
 *
 * Backlog #39 step 2. Every function here replaces logic that was either
 * duplicated across the three paywall surfaces, computed and then never
 * rendered, or absent entirely:
 *
 *   - pickDefaultPlan     — web had NO pre-selection at all (`useState(null)`,
 *                           CTA shipped disabled); mobile's two paywalls each
 *                           hardcoded their own default.
 *   - getPerMonthRate     — the onboarding paywall computed a per-month string
 *                           and never rendered it, so its whole comparison was
 *                           $16.99 vs $44.99 vs $129.99 in absolute terms.
 *   - calcTermDiscountPct — nine near-identical copies: one in ChoosePlan, six
 *                           in Onboarding/Plan, two on web.
 *   - getPlanBadges       — web computed isPopular/isBestValue and rendered
 *                           neither; mobile's crown was commented out.
 *
 * Consumes the canonical period vocabulary from ../analytics/subscriptionEvents
 * so presentation, `plans_shown` and `plan_type` all agree.
 */

import { toCanonicalPeriod, getPeriodMonths } from '../analytics/subscriptionEvents';

/**
 * Default-plan preference, most preferred first.
 *
 * Quarterly leads because it is the mid-tier commitment: it retains ~3x better
 * than monthly (23.1% vs 70.8% cancelled, web April-2026 cohort) and is worth
 * ~58% more ($57.89 vs $36.54 LTV over a matched window), while still being a
 * far smaller ask than annual. When quarterly is absent we step to the next
 * shortest multi-month term rather than to the most expensive one — defaulting
 * a user onto annual because quarterly happened to be missing would be a much
 * bigger ask than the product intends.
 *
 * `lifetime` sits behind `monthly` deliberately: it is a one-time purchase, not
 * a subscription term, and auto-selecting the single most expensive product on
 * the paywall is exactly the over-ask this ordering exists to avoid. That does
 * mean an offering of only monthly + lifetime defaults to monthly — see
 * getPlanBadges, which declines to badge monthly as "most popular" whenever a
 * longer term is present, so we never advertise the plan we steer away from.
 */
const DEFAULT_PLAN_PREFERENCE = ['quarterly', 'semiannual', 'annual', 'monthly', 'weekly', 'lifetime'];

/** Longest-first, for "best value" resolution. */
const DURATION_DESC = ['lifetime', 'annual', 'semiannual', 'quarterly', 'monthly', 'weekly'];

/**
 * Fewest distinct tiers an offering must have before BEST_VALUE is awarded.
 *
 * A "best value" stamp presumes a ladder the buyer can climb. On a short
 * offering — three tiers or fewer, e.g. aicerts / isaca [monthly, quarterly,
 * annual] — the top term is only one step above the popular one, so the badge
 * over-sells a two-step choice and MOST_POPULAR should carry the paywall alone.
 * Counted in distinct canonical periods, so it is identical for the subscription
 * and one-time lists (web toggle parity).
 */
const MIN_TIERS_FOR_BEST_VALUE = 4;

/**
 * Choose which plan a paywall should pre-select.
 *
 * Never returns null when anything is on offer: a paywall with nothing selected
 * is what web shipped, and it left the CTA disabled reading "Select a Plan to
 * Continue" with 8 equally-weighted options and no steer.
 *
 * @param {Array<string>} availablePeriods period labels in any dialect
 * @returns {string|null} canonical period, or null only if nothing is offered
 */
export const pickDefaultPlan = (availablePeriods) => {
  if (!Array.isArray(availablePeriods)) return null;
  const available = new Set(
    availablePeriods.map(toCanonicalPeriod).filter((p) => p !== 'unknown'),
  );
  if (available.size === 0) return null;
  return DEFAULT_PLAN_PREFERENCE.find((p) => available.has(p)) ?? null;
};

/**
 * Which plan stays selected when the web paywall toggles payment mode.
 *
 * A period may exist in one mode only, so on switch: keep the current term if the
 * target mode offers it; else fall back to the steer (pickDefaultPlan of the
 * target mode); else the first available term; else null (target mode empty).
 * Pure extraction of PricingGrid.handleToggle's inline logic so it is testable.
 *
 * @param {string|null} currentPeriod canonical period selected before the switch
 * @param {Array<string>} targetPeriods period labels offered in the target mode
 * @param {string|null} [steerPeriod] canonical steer for the target mode
 * @returns {string|null} canonical period to select, or null when nothing is offered
 */
export const resolveModeSwitchSelection = (currentPeriod, targetPeriods, steerPeriod) => {
  if (!Array.isArray(targetPeriods) || targetPeriods.length === 0) return null;
  const available = targetPeriods.map(toCanonicalPeriod).filter((p) => p !== 'unknown');
  if (available.length === 0) return null;
  if (currentPeriod && available.includes(currentPeriod)) return currentPeriod;
  if (steerPeriod && available.includes(steerPeriod)) return steerPeriod;
  return available[0];
};

/**
 * Per-month equivalent of a plan's total price.
 *
 * The number that makes a longer term legible. Takes an already-numeric price
 * (see resolvePurchasePrice) — never a display string, which is what the old
 * `price.substring(0, 1)` / comma-stripping helpers did and why they produced
 * "4 1499.67 /month" in comma-decimal locales.
 *
 * @param {number} price total price for the term, in major units
 * @param {number} months billing months (see getPeriodMonths)
 * @returns {number|null} null when it cannot be computed — never a silent 0
 */
export const getPerMonthRate = (price, months) => {
  if (typeof price !== 'number' || !Number.isFinite(price)) return null;
  if (typeof months !== 'number' || !Number.isFinite(months) || months <= 0) return null;
  return price / months;
};

/**
 * Discount of a term plan against paying monthly for the same span.
 *
 * @param {number} monthlyPrice price of one month
 * @param {number} planPrice total price of the term
 * @param {number} months months the term covers
 * @returns {number|null} whole percent, or null when not derivable
 */
export const calcTermDiscountPct = (monthlyPrice, planPrice, months) => {
  if (typeof monthlyPrice !== 'number' || !Number.isFinite(monthlyPrice) || monthlyPrice <= 0) return null;
  if (typeof planPrice !== 'number' || !Number.isFinite(planPrice) || planPrice < 0) return null;
  if (typeof months !== 'number' || !Number.isFinite(months) || months <= 0) return null;
  const baseline = monthlyPrice * months;
  if (baseline <= 0) return null;
  const pct = ((baseline - planPrice) / baseline) * 100;
  // A term that costs more than paying monthly is not a discount. Report null
  // rather than a negative badge.
  if (pct <= 0) return null;
  return Math.round(pct);
};

/**
 * Absolute-dollar saving of a term plan against paying monthly for the same span.
 *
 * The dollar sibling of calcTermDiscountPct — same guards, but returns the raw
 * amount saved (major units) so a paywall can render "Save $10" instead of a
 * percent. Callers format/round for display (e.g. formatMoney with 0 fraction
 * digits); this stays an exact number.
 *
 * @param {number} monthlyPrice price of one month
 * @param {number} planPrice total price of the term
 * @param {number} months months the term covers
 * @returns {number|null} amount saved in major units, or null when not derivable
 */
export const calcTermSavings = (monthlyPrice, planPrice, months) => {
  if (typeof monthlyPrice !== 'number' || !Number.isFinite(monthlyPrice) || monthlyPrice <= 0) return null;
  if (typeof planPrice !== 'number' || !Number.isFinite(planPrice) || planPrice < 0) return null;
  if (typeof months !== 'number' || !Number.isFinite(months) || months <= 0) return null;
  const savings = monthlyPrice * months - planPrice;
  // A term that costs more than paying monthly is not a saving.
  if (savings <= 0) return null;
  return savings;
};

/** Badge identifiers. Apps own the copy; core owns which plan gets which. */
export const PLAN_BADGE = {
  MOST_POPULAR: 'MOST_POPULAR',
  BEST_VALUE: 'BEST_VALUE',
};

/**
 * Which plan carries which badge.
 *
 * MOST_POPULAR goes to the default (the plan we are steering to). BEST_VALUE
 * goes to the longest term on offer, and is withheld when (a) it would collide
 * with MOST_POPULAR — two badges on one card reads as noise — or (b) the
 * offering has fewer than MIN_TIERS_FOR_BEST_VALUE distinct tiers, where the top
 * term is too close to the popular one to earn the label. A three-tier brand
 * (aicerts / isaca [monthly, quarterly, annual]) therefore shows MOST_POPULAR
 * only; a four-tier brand ([+ semiannual]) badges its longest term.
 *
 * @param {Array<string>} availablePeriods period labels in any dialect
 * @returns {Object<string,string>} canonical period -> badge id
 */
export const getPlanBadges = (availablePeriods) => {
  if (!Array.isArray(availablePeriods)) return {};
  const available = new Set(
    availablePeriods.map(toCanonicalPeriod).filter((p) => p !== 'unknown'),
  );
  if (available.size === 0) return {};

  const badges = {};
  const popular = pickDefaultPlan(availablePeriods);
  const longest = DURATION_DESC.find((p) => available.has(p));

  // Never stamp MOST_POPULAR on monthly while a longer term is on offer. The
  // default can legitimately land on monthly (e.g. an offering of only monthly
  // + lifetime, where defaulting to lifetime would be a worse over-ask), but
  // badging it would advertise the plan this whole module steers away from.
  const monthlyDespiteLonger = popular === 'monthly' && longest !== 'monthly';
  if (popular && !monthlyDespiteLonger) badges[popular] = PLAN_BADGE.MOST_POPULAR;

  // BEST_VALUE only on a genuinely laddered offering (>= 4 tiers), and never
  // doubled up on the card MOST_POPULAR already owns.
  const laddered = available.size >= MIN_TIERS_FOR_BEST_VALUE;
  if (longest && longest !== popular && laddered) badges[longest] = PLAN_BADGE.BEST_VALUE;

  return badges;
};

/**
 * Everything a paywall card needs to render one plan, derived consistently.
 *
 * @param {object} input
 * @param {string} input.period          period label in any dialect
 * @param {number} input.price           total price for the term, major units
 * @param {number} [input.monthlyPrice]  price of the monthly plan, for the discount
 * @param {Object<string,string>} [input.badges] output of getPlanBadges
 * @returns {{period: string, months: number|null, perMonth: number|null,
 *            discountPct: number|null, savings: number|null, badge: string|null}}
 */
export const buildPlanCard = ({ period, price, monthlyPrice, badges } = {}) => {
  const canonical = toCanonicalPeriod(period);
  const months = getPeriodMonths(canonical);
  return {
    period: canonical,
    months,
    perMonth: getPerMonthRate(price, months),
    // Monthly is its own baseline, so it never carries a discount/saving.
    discountPct: canonical === 'monthly' ? null : calcTermDiscountPct(monthlyPrice, price, months),
    savings: canonical === 'monthly' ? null : calcTermSavings(monthlyPrice, price, months),
    badge: badges?.[canonical] ?? null,
  };
};

/**
 * Terms a brand offers when it does not configure `SUBSCRIPTION_PLANS`.
 *
 * Mirrors the fallback ChoosePlan carried inline. Three tiers, no 6-month: a
 * brand has to opt IN to the laddered offering, not out of it.
 */
export const DEFAULT_SUBSCRIPTION_PLANS = [1, 3, 12];

/**
 * Whether a plan term is offered by a brand.
 *
 * Mobile brands describe their paywall as a list of billing-month counts
 * (`APP_DATA.SUBSCRIPTION_PLANS`, e.g. [1, 3, 12]); the RevenueCat offering can
 * carry MORE packages than that — notably a 6-month package that must stay on
 * sale for existing subscribers (Apple discontinues renewals for everyone when
 * a subscription is removed from sale) but is no longer merchandised. This is
 * the single gate every MOBILE paywall surface must apply BEFORE it derives
 * anything from the package list: default selection, badges, `plans_shown` and
 * the cards themselves. The apps reach it through one loader
 * (lzapps_common_code/utils/offerings.js) so no screen reads the raw offering.
 * Before it existed, the onboarding paywall applied no gate at all and kept
 * showing the hidden tier.
 *
 * Web does NOT read SUBSCRIPTION_PLANS: its tier set is the ACTIVE Stripe
 * prices mapped through configs/<brand>/app_constant/prices.js, so dropping a
 * tier on web means archiving the Stripe price and trimming that table
 * (backlog #58 tracks unifying the two).
 *
 * Terms without a month count (weekly, lifetime) are never offered by this
 * gate — no brand merchandises them on the term ladder — and an empty list
 * (white-label) offers nothing.
 *
 * @param {string} period period label in any dialect (e.g. RC `SIX_MONTH`)
 * @param {Array<number>|null|undefined} [subscriptionPlans] brand month list;
 *        null/undefined -> DEFAULT_SUBSCRIPTION_PLANS
 * @returns {boolean}
 */
export const isPlanOffered = (period, subscriptionPlans) => {
  const months = getPeriodMonths(period);
  if (months === null) return false;
  const plans = Array.isArray(subscriptionPlans) ? subscriptionPlans : DEFAULT_SUBSCRIPTION_PLANS;
  return plans.includes(months);
};

/**
 * Period label of an offering item: a RevenueCat package (`packageType`), a web
 * product row (`period`) or a bare label. One default accessor so both
 * platforms can pass their items as-is.
 */
const defaultPeriodOf = (item) => item?.packageType ?? item?.period ?? item;

/**
 * The subset of an offering a brand actually merchandises, order preserved.
 *
 * @param {Array<T>} items packages / product rows / labels from the live offering
 * @param {Array<number>|null|undefined} subscriptionPlans brand month list
 * @param {(item: T) => string} [getPeriod] label accessor; defaults to
 *        `packageType` (RC), then `period` (web), then the item itself (label)
 * @returns {Array<T>}
 * @template T
 */
export const filterOfferedPlans = (items, subscriptionPlans, getPeriod = defaultPeriodOf) => {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => isPlanOffered(getPeriod(item), subscriptionPlans));
};

/**
 * The item a paywall should pre-select: the one whose period pickDefaultPlan
 * chooses from the (already gated) offering. Item-level sibling of
 * pickDefaultPlan, so screens stop re-deriving canonical periods to find the
 * package behind the steer.
 *
 * @param {Array<T>} items gated offering (see filterOfferedPlans)
 * @param {(item: T) => string} [getPeriod] label accessor (same default)
 * @returns {T|null} null when nothing is offered
 * @template T
 */
export const pickDefaultPackage = (items, getPeriod = defaultPeriodOf) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  const defaultPeriod = pickDefaultPlan(items.map(getPeriod));
  if (!defaultPeriod) return null;
  return items.find((item) => toCanonicalPeriod(getPeriod(item)) === defaultPeriod) ?? null;
};
